export class Http {
  // Helper to safely parse JSON response, returning null for non-JSON bodies
  private static async parseJsonSafe<T>(response: Response): Promise<T | null> {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as T;
    } catch {
      // Response body is not JSON (e.g. "Jwt is missing" plain text from Envoy)
      return null;
    }
  }

  static async get<T>(
    url: string,
    headers?: Record<string, string>,
    options?: { signal?: AbortSignal }
  ): Promise<{ status: number; data: T | null }> {
    const response = await fetch(url, {
      method: 'GET',
      headers: headers || {},
      signal: options?.signal,
    });
    const data = await Http.parseJsonSafe<T>(response);
    return { status: response.status, data };
  }

  static async post<T, R>(
    url: string,
    payload: T,
    headers?: Record<string, string>,
    options?: { signal?: AbortSignal }
  ): Promise<{ status: number; data: R | null }> {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: { ...defaultHeaders, ...headers },
      body: JSON.stringify(payload),
      signal: options?.signal,
    });

    const data = await Http.parseJsonSafe<R>(response);
    return { status: response.status, data };
  }

  static async patch<T, R>(
    url: string,
    payload: T,
    headers?: Record<string, string>
  ): Promise<{ status: number; data: R | null }> {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'PATCH',
      headers: { ...defaultHeaders, ...headers },
      body: JSON.stringify(payload),
    });

    const data = await Http.parseJsonSafe<R>(response);
    return { status: response.status, data };
  }

  static async put<T, R>(
    url: string,
    payload: T,
    headers?: Record<string, string>
  ): Promise<{ status: number; data: R | null }> {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'PUT',
      headers: { ...defaultHeaders, ...headers },
      body: JSON.stringify(payload),
    });

    const data = await Http.parseJsonSafe<R>(response);
    return { status: response.status, data };
  }

  static async delete<T, R>(
    url: string,
    payload: T,
    headers?: Record<string, string>
  ): Promise<{ status: number; data: R | null }> {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'DELETE',
      headers: { ...defaultHeaders, ...headers },
      body: JSON.stringify(payload),
    });

    const data = await Http.parseJsonSafe<R>(response);
    return { status: response.status, data };
  }
}
